import { Component, inject, input, signal, SimpleChanges, viewChild } from '@angular/core';
import { AddResumeSkill } from 'src/app/models/resume/add-resume';
import { ResumeService } from 'src/app/services/resume.service';
import { ResumeEducation, ResumeLanguage, ResumeSkill } from 'src/app/models/resume/resume';
import Swal from 'sweetalert2';
import { ModalSkillComponent } from './modal-skill/modal-skill.component';
import { ModalLanguageComponent } from './modal-language/modal-language.component';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-cv-skills',
  imports: [ModalSkillComponent, ModalLanguageComponent],
  templateUrl: './cv-skills.component.html',
  styleUrl: './cv-skills.component.css'
})
export class CvSkillsComponent {
  public resumeId = input<number | null>(null);
  educations = signal<ResumeEducation[]>([]);
  skills = signal<ResumeSkill[]>([]);
  softSkills = signal<ResumeSkill[]>([]);
  languages = signal<ResumeLanguage[]>([]);

  indexResume = signal<number | null>(null);

  modalSkillComponent = viewChild(ModalSkillComponent);
  modalLanguageComponent = viewChild(ModalLanguageComponent);

  private readonly resumeService = inject(ResumeService);
  private toastr = inject(ToastrService);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['resumeId']?.currentValue) {
      this.getResumeSkills();
      this.getResumeSoftSkills();
      this.getResumeLanguages();
    }
  }

  getResumeSkills() {
    const resumeId = this.resumeId();
    if (!resumeId) {
      return;
    }
    this.resumeService.getResumeSkills(resumeId).subscribe({
      next: res => {
        this.skills.set(res);
      }
    });
  }

  getResumeSoftSkills() {
    const resumeId = this.resumeId();
    if (!resumeId) {
      return;
    }
    this.resumeService.getResumeSkills(resumeId, true).subscribe({
      next: res => {
        this.softSkills.set(res);
      }
    });
  }

  getResumeLanguages() {
    const resumeId = this.resumeId();
    if (!resumeId) {
      return;
    }
    this.resumeService.getResumeLanguages(resumeId).subscribe({
      next: res => {
        this.languages.set(res);
      }
    });
  }

  addLanguage() {
    this.indexResume.set(null);
    if (this.modalLanguageComponent()) {
      this.modalLanguageComponent()?.reset();
    }
  }

  addResumeSkill(softSkill: boolean = false) {
    this.indexResume.set(null);
    if (this.modalSkillComponent()) {
      this.modalSkillComponent()?.reset();
      this.modalSkillComponent()?.softSkill.set(softSkill);
      this.modalSkillComponent()?.getOptions();
    }
  }

  editResumeSkill(resumeSkill: AddResumeSkill, index: number, softSkill: boolean = false) {
    this.indexResume.set(index);
    if (this.modalSkillComponent()) {
      this.modalSkillComponent()?.softSkill.set(softSkill);
      this.modalSkillComponent()?.getOptions();
      this.modalSkillComponent()?.form.patchValue({
        ...resumeSkill as any
      }, {
        onlySelf: true,
        emitEvent: false
      });
    }
  }

  deleteResumeSkill(resumeSkill: ResumeSkill, index: number, softSkill: boolean = false) {
    const resumeId = this.resumeId();
    const id = resumeSkill.id ?? 0;

    if (!resumeId) {
      if (!softSkill) {
        this.skills.update(value => value?.filter((skill, i) => i !== index));
      } else {
        this.softSkills.update(value => value?.filter((skill, i) => i !== index));
      }
      return;
    }

    Swal.fire({
      title: 'Eliminar',
      text: '¿Desea eliminar este registro?',
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: '#0d1174',
      cancelButtonColor: '#3a3a3a',
      confirmButtonText: 'Eliminar'
    }).then((resultmodal) => {
      if (resultmodal.isConfirmed) {
        this.resumeService.deleteResumeSkill(id).subscribe({
          next: res => {
            if (!softSkill) {
              this.skills.update(value => value?.filter((skill, i) => i !== index));
            } else {
              this.softSkills.update(value => value?.filter((skill, i) => i !== index));
            }
          }, error: res => {
            Swal.fire({
              title: 'Error',
              text: 'Ocurrió un error al eliminar el registro.',
              icon: 'error',
              confirmButtonColor: '#d33'
            });
          }
        })
      }
    });
  }

  handlerResumeSkill(resumeSkill: ResumeSkill) {
    const resumeId = this.resumeId();
    if (!resumeSkill.softSkill) {
      if (resumeId) {
        this.getResumeSkills();
        return;
      }
      if (resumeSkill.id || this.indexResume() !== null) {
        this.skills.update(value => value?.map((skill, i) => {
          const isMatch = resumeSkill.id ? skill.id === resumeSkill.id : i === this.indexResume();
          return isMatch ? resumeSkill : skill;
        }));
        this.indexResume.set(null); 
        return;
      }
      if (this.skills().length >= 10) {
        this.toastr.warning("Se ha alcanzado el límite máximo de 10 habilidades permitidas.");
        return;
      }
      this.skills.set([...this.skills(), resumeSkill]);
    } else {
      if (resumeId) {
        this.getResumeSoftSkills();
        return;
      }
      if (resumeSkill.id || this.indexResume() !== null) {
        this.softSkills.update(value => value?.map((skill, i) => {
          const isMatch = resumeSkill.id ? skill.id === resumeSkill.id : i === this.indexResume();
          return isMatch ? resumeSkill : skill;
        }));
        this.indexResume.set(null); 
        return;
      }
      this.softSkills.set([...this.softSkills(), resumeSkill]);
    }
  }

  editResumeLanguage(resumeLanguage: ResumeLanguage, index: number) {
    this.indexResume.set(index);
    if (this.modalLanguageComponent()) {
      this.modalLanguageComponent()?.form.patchValue({
        ...resumeLanguage as any
      }, {
        onlySelf: true,
        emitEvent: false
      });
    }
  }

  deleteResumeLanguage(resumeLanguage: ResumeLanguage, index: number) {
    const resumeId = this.resumeId();
    const id = resumeLanguage.id ?? 0;

    if (!resumeId) {
      this.languages.update(value => value?.filter((language, i) => i !== index));
      return;
    }

    Swal.fire({
      title: 'Eliminar',
      text: '¿Desea eliminar este registro?',
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: '#0d1174',
      cancelButtonColor: '#3a3a3a',
      confirmButtonText: 'Eliminar'
    }).then((resultmodal) => {
      if (resultmodal.isConfirmed) {
        this.resumeService.deleteResumeLanguage(id).subscribe({
          next: res => {
            this.languages.update(languages => languages.filter(language => language.id !== id));
          }, error: res => {
            Swal.fire({
              title: 'Error',
              text: 'Ocurrió un error al eliminar el registro.',
              icon: 'error',
              confirmButtonColor: '#d33'
            });
          }
        })
      }
    });
  }

  handlerResumeLanguage(resumeLanguage: ResumeLanguage) {
    const resumeId = this.resumeId();
    if (resumeId) {
      this.getResumeLanguages();
      return;
    }
    if (this.indexResume() != null || resumeLanguage.id) {
      this.languages.update(value => value?.map((language, i) => {
        if (i === this.indexResume() || language.id === resumeLanguage.id) {
          return resumeLanguage
        }
        return language;
      }));
      return;
    }
    this.languages.set([...this.languages(), resumeLanguage]);
  }
}